import { createElement } from 'react';

import {
  getTypographyClasses,
  type TypographyVariant,
} from '@/shared/config/typography';
import { classNames } from '@/shared/lib/classNames/classNames';

type TypographyOwnProps = {
  /** Вариант типографики */
  variant?: TypographyVariant;
  /** Цвет текста */
  color?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'inverse-primary'
    | 'inverse-secondary';
  /** Дополнительные классы */
  className?: string;
  /** Дополнительные стили */
  style?: React.CSSProperties;
  /** Контент */
  children?: React.ReactNode;
};

export const TYPOGRAPHY_CLASS_BY_VARIANT_MAP: Record<
  TypographyVariant,
  string
> = {
  'heading-xl': 'typo-heading-xl',
  'heading-l': 'typo-heading-l',
  'heading-m': 'typo-heading-m',
  'heading-s': 'typo-heading-s',
  'heading-xs': 'typo-heading-xs',
  'text-base': 'typo-base',
  'text-l': 'typo-text-l',
  'text-m': 'typo-text-m',
  'text-s': 'typo-text-s',
  'text-xs': 'typo-text-xs',
  'accent-base': 'typo-accent-base',
  'accent-l': 'typo-accent-l',
  'accent-m': 'typo-accent-m',
  'accent-s': 'typo-accent-s',
  'accent-xs': 'typo-accent-xs',
};

export type TypographyProps<T extends React.ElementType = 'div'> = {
  /** HTML элемент (по умолчанию определяется автоматически на основе variant) */
  as?: T;
} & TypographyOwnProps &
  Omit<React.ComponentPropsWithoutRef<T>, keyof TypographyOwnProps | 'as'>;

const defaultElementMap: Record<string, React.ElementType> = {
  'heading-xl': 'h1',
  'heading-l': 'h2',
  'heading-m': 'h3',
  'heading-s': 'h4',
  'text-base': 'p',
  'text-l': 'p',
  'text-m': 'p',
  'text-s': 'span',
  'text-xs': 'span',
  'accent-base': 'span',
  'accent-l': 'span',
  'accent-m': 'span',
  'accent-s': 'span',
  'accent-xs': 'span',
};

const colorClasses = {
  primary: 'text-text-base-primary',
  secondary: 'text-text-base-secondary',
  tertiary: 'text-text-base-tertiary',
  'inverse-primary': 'text-text-base-inverse-primary',
  'inverse-secondary': 'text-text-base-inverse-secondary',
};

export const Typography = <T extends React.ElementType = 'div'>({
  variant = 'text-base',
  as,
  color = 'primary',
  className,
  style,
  children,
  ...restProps
}: TypographyProps<T>) => {
  const element = as || defaultElementMap[variant] || 'div';
  const typographyClasses = getTypographyClasses(variant);
  const colorClass = colorClasses[color];

  return createElement(
    element,
    {
      className: classNames({
        cls: typographyClasses,
        additional: [colorClass, className],
      }),
      style,
      ...restProps,
    },
    children,
  );
};
